import { compareStopsByDistance } from "./stopDistance.js";

export function normalizeStopSearchText(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getStopSearchMatchRank(stop, query) {
  const normalizedQuery = normalizeStopSearchText(query);
  const code = normalizeStopSearchText(stop.stop_code);
  const preferredName = normalizeStopSearchText(stop.preferred_name);
  const name = normalizeStopSearchText(stop.stop_name);

  if (code === normalizedQuery) return 0;
  if (code.startsWith(normalizedQuery)) return 1;
  if (preferredName.startsWith(normalizedQuery)) return 2;
  if (name.startsWith(normalizedQuery)) return 3;
  return 4;
}

export function matchesStopSearch(stop, query) {
  const normalizedQuery = normalizeStopSearchText(query);
  if (!normalizedQuery) return true;
  const code = normalizeStopSearchText(stop.stop_code);
  const preferredName = normalizeStopSearchText(stop.preferred_name);
  const name = normalizeStopSearchText(stop.stop_name);
  const parts = normalizedQuery
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  const matchesName = (value) => {
    const words = value.replace(/[^\p{L}\p{N}]+/gu, " ").split(/\s+/);
    return parts.every((part) => words.some((word) => word.startsWith(part)));
  };
  return (
    code.startsWith(normalizedQuery) ||
    (parts.length > 0 && (matchesName(preferredName) || matchesName(name)))
  );
}

export function mergeMapSearchResults(stopResults, addressResults, limit = 15) {
  return [...(stopResults || []), ...(addressResults || [])].slice(0, limit);
}

export function rankStopSearchResults(stops, query) {
  return [...stops].sort(
    (a, b) =>
      getStopSearchMatchRank(a, query) - getStopSearchMatchRank(b, query) ||
      normalizeStopSearchText(a.preferred_name || a.stop_name).localeCompare(
        normalizeStopSearchText(b.preferred_name || b.stop_name),
      ),
  );
}

const DEFAULT_STOP_SEARCH_LIMIT = 10;
const MAX_STOP_SEARCH_LIMIT = 20;

function parseCoordinate(value, maximum) {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  if (String(value).trim() === "") return undefined;
  const number = Number(value);
  return Number.isFinite(number) && Math.abs(number) <= maximum
    ? number
    : undefined;
}

/**
 * No query means a complete catalogue for local home search and favourites.
 * Typed queries rank all matches before limiting: text relevance, regional
 * relevance, optional proximity, then name/ID. Coordinates never restrict the
 * catalogue. Invalid/incomplete coordinates disable proximity ranking.
 */
export function selectStopSearchResults(stops, query, options = {}) {
  const rows = Array.isArray(stops) ? stops : [];
  const normalizedQuery = normalizeStopSearchText(query);
  if (!normalizedQuery) return rows;

  const requestedLimit = typeof options.limit === "string" || typeof options.limit === "number"
    ? Number(options.limit)
    : NaN;
  const limit = Number.isInteger(requestedLimit) && requestedLimit > 0
    ? Math.min(requestedLimit, MAX_STOP_SEARCH_LIMIT)
    : DEFAULT_STOP_SEARCH_LIMIT;
  const latitude = parseCoordinate(options.lat, 90);
  const longitude = parseCoordinate(options.lng, 180);
  const hasOrigin = latitude !== undefined && longitude !== undefined;
  const matches = options.search
    ? options.search(rows, normalizedQuery)
    : rows.filter((stop) => matchesStopSearch(stop, normalizedQuery));

  return [...matches].sort((a, b) =>
    getStopSearchMatchRank(a, normalizedQuery) - getStopSearchMatchRank(b, normalizedQuery) ||
    (options.compareRegional?.(a, b, normalizedQuery) || 0) ||
    (hasOrigin ? compareStopsByDistance({ a, b, latitude, longitude }) : 0) ||
    normalizeStopSearchText(a.preferred_name || a.stop_name).localeCompare(
      normalizeStopSearchText(b.preferred_name || b.stop_name),
    ) ||
    String(a.stop_id).localeCompare(String(b.stop_id)),
  ).slice(0, limit);
}

export function rankMapSearchSuggestions(suggestions, query) {
  return [...suggestions].sort((a, b) => {
    if (a.type !== b.type) return a.type === "stop" ? -1 : 1;
    return a.type === "stop"
      ? getStopSearchMatchRank(a, query) - getStopSearchMatchRank(b, query)
      : 0;
  });
}

export function getMapSuggestionText(suggestion) {
  if (suggestion?.type !== "Feature") return suggestion?.stop_name || null;

  const clean = (value) => String(value || "").replace(/\s+\d{4}$/, "").trim();
  const parts = String(suggestion.place_name || "").split(",").map(clean).filter(Boolean);
  let name = clean(suggestion.text) || parts[0] || "";
  // An address feature may supply just the house number separately. POIs can
  // supply a whole street address; don't append that to an already named place.
  if (suggestion.place_type?.includes("address") &&
      /^\d+[a-z]?(?:[-/]\d+[a-z]?)?$/i.test(String(suggestion.address || "")) &&
      !name.startsWith(`${suggestion.address} `)) {
    name = `${suggestion.address} ${name}`;
  }
  const context = Array.isArray(suggestion.context) ? suggestion.context : [];
  const locality = ["neighborhood", "locality", "place"]
    .map(kind => context.find(item => String(item.id || "").startsWith(`${kind}.`)))
    .find(Boolean);
  // Older cached results may contain only place_name. Keep at most its first
  // locality, removing postcode/country rather than showing the full address.
  const suburb = clean(locality?.text) ||
    (parts.length > 1 && !/^(new zealand|nz)$/i.test(parts[1]) ? parts[1] : "");
  return suburb && normalizeStopSearchText(suburb) !== normalizeStopSearchText(name)
    ? `${name}, ${suburb}` : name;
}
